import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

interface ScrollToTopProps {
  /**
   * Enable/disable smooth scrolling behavior
   * @default true
   */
  smooth?: boolean;

  /**
   * Custom scroll position in pixels
   * @default 0
   */
  position?: number;

  /**
   * Callback function executed after scroll is complete
   */
  onScrollComplete?: () => void;

  /**
   * Array of pathnames where scroll behavior should be disabled
   * @default []
   */
  excludePaths?: string[];

  /**
   * Delay in milliseconds before initiating scroll
   * @default 0
   */
  scrollDelay?: number;

  /**
   * Maximum time in milliseconds to wait for smooth scroll completion
   * @default 1000
   */
  scrollTimeout?: number;
}

const ScrollToTop: React.FC<ScrollToTopProps> = ({
  smooth = true,
  position = 0,
  onScrollComplete,
  excludePaths = [],
  scrollDelay = 0,
  scrollTimeout = 1000,
}) => {
  const { pathname } = useLocation();
  const hasScrolledRef = useRef(false);
  const checkIntervalRef = useRef<ReturnType<typeof setInterval>>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const clearTimers = () => {
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = undefined;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  };

  useEffect(() => {
    // Reset the scroll flag when the pathname changes
    hasScrolledRef.current = false;

    // Don't scroll if current path is in exclude list
    if (excludePaths.includes(pathname)) {
      return;
    }

    // Prevent scrolling more than once per route change
    if (hasScrolledRef.current) {
      return;
    }

    // Mark as scrolled
    hasScrolledRef.current = true;

    // Validate position is a non-negative number
    const targetPosition = Math.max(0, position);

    // Clear any existing timers
    clearTimers();

    // Check if smooth scrolling is supported
    const supportsSmooth = "scrollBehavior" in document.documentElement.style;
    const shouldUseSmooth = smooth && supportsSmooth;

    // Perform the scroll with delay
    const scrollTimer = setTimeout(() => {
      try {
        window.scrollTo({
          top: targetPosition,
          left: 0,
          behavior: shouldUseSmooth ? "smooth" : "auto",
        });

        // For instant scroll or unsupported smooth scroll, call callback immediately
        if (!shouldUseSmooth) {
          onScrollComplete?.();
          return;
        }

        // For smooth scrolling, wait for scroll to complete
        const checkInterval = setInterval(() => {
          if (Math.abs(window.scrollY - targetPosition) < 1) {
            clearInterval(checkInterval);
            clearTimeout(completeTimer);
            onScrollComplete?.();
          }
        }, 100);

        checkIntervalRef.current = checkInterval;

        // Cleanup interval after specified timeout
        const completeTimer = setTimeout(() => {
          if (checkIntervalRef.current) {
            clearInterval(checkIntervalRef.current);
            onScrollComplete?.();
          }
        }, scrollTimeout);

        timeoutRef.current = completeTimer;
      } catch (error) {
        console.error("Error during scroll operation:", error);
        // Fallback to instant scroll
        window.scrollTo(0, targetPosition);
        onScrollComplete?.();
      }
    }, scrollDelay);

    timeoutRef.current = scrollTimer;

    // Cleanup function
    return clearTimers;
  }, [pathname, excludePaths]);

  return null;
};

export default ScrollToTop;
